import { Box, Group, Text } from '@mantine/core'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'

interface CollapsibleSectionProps {
  title: React.ReactNode
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
  titleClassName?: string
  rightSection?: React.ReactNode
}

export const CollapsibleSection = ({
  title,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  children,
  titleClassName,
  rightSection,
}: CollapsibleSectionProps) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)

  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen

  const handleToggle = (e: React.MouseEvent) => {
    // Prevent toggling if clicking on interactive elements in rightSection
    const target = e.target as HTMLElement
    if (
      rightSection &&
      (target.closest('button') ||
        target.closest('[role="button"]') ||
        target.closest('.mantine-ActionIcon-root') ||
        target.closest('.mantine-Menu-target') ||
        target.closest('.mantine-Menu-dropdown') ||
        target.closest('.mantine-Menu-item') ||
        target.closest('[role="menu"]') ||
        target.closest('[role="menuitem"]'))
    ) {
      return
    }

    const newOpen = !isOpen
    if (onOpenChange) {
      onOpenChange(newOpen)
    } else {
      setInternalOpen(newOpen)
    }
  }

  return (
    <Box>
      <Group
        justify="space-between"
        mb="xs"
        wrap="nowrap"
        onClick={handleToggle}
        style={{ cursor: 'pointer' }}
      >
        <Group
          gap={4}
          style={{ flex: 1 }}
          className={
            titleClassName ||
            'text-doubleNickel-gray-500 hover:text-doubleNickel-gray-700'
          }
        >
          {isOpen ? (
            <ChevronDown size={12} className="flex-shrink-0" />
          ) : (
            <ChevronRight size={12} className="flex-shrink-0" />
          )}
          <Text size="xs" fw={400} className="text-inherit">
            {title}
          </Text>
        </Group>
        {rightSection}
      </Group>
      <Box
        style={{
          maxHeight: isOpen ? '2000px' : '0',
          overflow: 'hidden',
          transition: 'max-height 300ms ease-in-out',
        }}
      >
        {children}
      </Box>
    </Box>
  )
}
